import {
  createRouter,
  createWebHistory,
} from 'vue-router';
import { useAuthStore } from '@/views/auth/auth_store';
import { useMobileMenu } from '@/shared/composables/use_mobile_menu.js';
import { Role } from '@/enum/roles_enum.js';
import {
  canViewModule,
  canCreateModule,
} from '@/shared/permissions/permission.utils.js';
import HomePage from '@/views/home/home_page.vue';
import AuthPage from '@/views/auth/auth_page.vue';
import PageContainer from '@/shared/layout/page_container.vue';
import ProspectsPage from '@/views/prospects/prospects_page.vue';
import ProspectList from '@/views/prospects/prospect_list/prospect_list.vue';
import ProspectEdit from '@/views/prospects/prospect_edit/prospect_edit.vue';
import ProspectDetail from '@/views/prospects/prospect_detail/prospect_detail.vue';
import RequestPage from '@/views/requests/request_page.vue';
import RequestList from '@/views/requests/request_list/request_list.vue';
import RequestEdit from '@/views/requests/request_edit/request_edit.vue';
import PricesPage from '@/views/prices/prices_page.vue';
import PricesEdit from '@/views/prices/prices_edit/prices_edit.vue';
import PricesList from '@/views/prices/prices_list/prices_list.vue';
import ProposalsPage from '@/views/proposals/proposals_page.vue';
import ProposalsDetail from '@/views/proposals/proposals_detail/proposals_detail.vue';
import ProposalsEdit from '@/views/proposals/proposals_edit/proposals_edit.vue';
import ProposalsList from '@/views/proposals/proposals_list/proposals_list.vue';
import CylindersPage from '@/views/cylinders/cylinders_page.vue';
import CylindersList from '@/views/cylinders/cylinders_list/cylinders_list.vue';
import CylinderDetail from '@/views/cylinders/cylinder_detail/cylinder_detail.vue';
import CylinderEdit from '@/views/cylinders/cylinder_edit/cylinder_edit.vue';
import CylinderAdaptersPage from '@/views/cylinder_adapters/cylinder_adapters_page.vue';
import CylinderAdaptersList from '@/views/cylinder_adapters/cylinder_adapters_list/cylinder_adapters_list.vue';
import CylinderAdapterDetail from '@/views/cylinder_adapters/cylinder_adapter_detail/cylinder_adapter_detail.vue';
import CylinderAdapterEdit from '@/views/cylinder_adapters/cylinder_adapter_edit/cylinder_adapter_edit.vue';
import ResourcePage from '@/views/resource/resource_page.vue';
import ResourceList from '@/views/resource/resource_list/resource_list.vue';
import ResourceDetail from '@/views/resource/resource_detail/resource_detail.vue';
import ResourceEdit from '@/views/resource/resource_edit/resource_edit.vue';
import ReportsList from '@/views/reports/reports_list/reports_list.vue';
import ReportsPage from '@/views/reports/reports_page.vue';
import SwagePage from '@/views/swage/swage_page.vue';
import SwageList from '@/views/swage/swage_list/swage_list.vue';
import SwageEdit from '@/views/swage/swage_edit/swage_edit.vue';
import SwageDetail from '@/views/swage/swage_detail/swage_detail.vue';
import SettingPage from '@/views/setting/setting_page.vue';
import SettingList from '@/views/setting/setting_list/setting_list.vue';
import UsersPage from '@/views/users/users_page.vue';
import UserList from '@/views/users/users_list/user_list.vue';
import UserEdit from '@/views/users/user_edit/user_edit.vue';
import UserDetail from '@/views/users/user_detail/user_detail.vue';
import MaterialPage from '@/views/material/material_page.vue';
import MaterialList from '@/views/material/material_list/material_list.vue';
import MaterialDetail from '@/views/material/material_detail/material_detail.vue';
import MaterialEdit from '@/views/material/material_edit/material_edit.vue';
import WorkOrderPage from '@/views/work_orders/work_order_page.vue';
import WorkOrderList from '@/views/work_orders/work_order_list/work_order_list.vue';
import WorkOrderEdit from '@/views/work_orders/work_order_edit/work_order_edit.vue';
import TechnicalSpecificationPage from '@/views/technical_specifications/technical_specification_page.vue';
import TechnicalSpecificationList from '@/views/technical_specifications/technical_specification_list/technical_specification_list.vue';
import TechnicalSpecificationEdit from '@/views/technical_specifications/technical_specification_edit/technical_specification_edit.vue';
import SupplierPage from '@/views/supplier/supplier_page.vue';
import SupplierList from '@/views/supplier/supplier_list/supplier_list.vue';
import SupplierEdit from '@/views/supplier/supplier_edit/supplier_edit.vue';
import AgentPage from '@/views/agent/agent_page.vue';
import AgentList from '@/views/agent/agent_list/agent_list.vue';
import AgentEdit from '@/views/agent/agent_edit/agent_edit.vue';
import TipoTratadoPage from '@/views/tipo-tratado/tipo_tratado_page.vue';
import TipoTratadoList from '@/views/tipo-tratado/tipo_tratado_list/tipo_tratado_list.vue';
import TipoTratadoEdit from '@/views/tipo-tratado/tipo_tratado_edit/tipo_tratado_edit.vue';
import ColoresPage from '@/views/colores/colores_page.vue';
import ColoresList from '@/views/colores/colores_list/colores_list.vue';
import InventoryMovementPage from '@/views/inventory_movement/inventory_movement_page.vue';
import InventoryMovementList from '@/views/inventory_movement/inventory_movement_list/inventory_movement_list.vue';
import InventoryMovementEdit from '@/views/inventory_movement/inventory_movement_edit/inventory_movement_edit.vue';
import InventoryMovemenDetail from '@/views/inventory_movement/inventory_movement_detail/inventory_movement_detail.vue';
import DashboardPage from '@/views/dashboard/dashboard_page.vue';
import VistaGeneralProduccion from '@/views/prices/resultado_produccion/vista_general_produccion.vue';
import VistaGeneralProduccionOT from '@/views/work_orders/resultado_produccion/vista_general_produccion_ot.vue';

const meta = { requiresAuth: true, roles: [Role.ANY] };

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    { path: '/', name: 'login', component: AuthPage, meta: { requiresAuth: false } },
    { path: '/home', name: 'home', component: HomePage, meta },
    {
      path: '/dashboard',
      component: PageContainer,
      meta: { requiresAuth: true, moduleKey: 'prospectos' },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: DashboardPage,
          meta: { requiresAuth: true, moduleKey: 'prospectos' },
        },
      ],
    },
    {
      path: '/page',
      name: 'page',
      component: PageContainer,
      meta: { requiresAuth: false },
      children: [
        {
          path: 'prospects',
          name: 'prospects',
          component: ProspectsPage,
          meta: { requiresAuth: true, moduleKey: 'prospectos' },
          children: [
            { path: 'list', name: 'prospect', component: ProspectList },
            { path: 'detail/:id', name: 'prospectDetail', component: ProspectDetail },
            { path: 'edit/:id?', name: 'prospectEdit', component: ProspectEdit },
          ],
        },
        {
          path: 'requests',
          name: 'requests',
          component: RequestPage,
          meta: { requiresAuth: true, moduleKey: 'solicitud' },
          children: [
            { path: 'list', name: 'requestList', component: RequestList, props: true },
            { path: 'edit/:id?', name: 'requestEdit', component: RequestEdit },
          ],
        },
        {
          path: 'prices',
          name: 'prices',
          component: PricesPage,
          meta: { requiresAuth: true, moduleKey: 'cotizacion' },
          children: [
            { path: 'list', name: 'pricesList', component: PricesList, props: true },
            { path: 'edit/:id?', name: 'pricesEdit', component: PricesEdit },
            {
              path: 'resultado-produccion/:codigo',
              name: 'vistaGeneralProduccion',
              component: VistaGeneralProduccion,
            },
          ],
        },
        {
          path: 'proposals',
          name: 'proposals',
          component: ProposalsPage,
          meta: { requiresAuth: true, moduleKey: 'propuestas' },
          children: [
            { path: 'list', name: 'proposalsList', component: ProposalsList, props: true },
            { path: 'detail/:id', name: 'proposalsDetail', component: ProposalsDetail },
            { path: 'edit/:id?', name: 'proposalsEdit', component: ProposalsEdit },
          ],
        },
        {
          path: 'resource',
          name: 'resource',
          component: ResourcePage,
          meta: { requiresAuth: true, moduleKey: 'recursos' },
          children: [
            { path: 'list', name: 'resourceList', component: ResourceList, meta },
            { path: 'detail/:id', name: 'resourceDetail', component: ResourceDetail, meta },
            { path: 'edit/:id', name: 'resourceEdit', component: ResourceEdit, meta },
          ],
        },
        {
          path: 'cylinders',
          name: 'cylinders',
          component: CylindersPage,
          meta: { requiresAuth: true, moduleKey: 'cilindros' },
          children: [
            { path: 'list', name: 'cylinderList', component: CylindersList, meta },
            {
              path: 'detail/:CodigoCilindroTipo/:CodigoDesarrollo',
              name: 'cilindroDetail',
              component: CylinderDetail,
              meta,
            },
            {
              path: 'edit/:CodigoCilindroTipo?/:CodigoDesarrollo?',
              name: 'cilindroEdit',
              component: CylinderEdit,
              meta,
            },
          ],
        },
        {
          path: 'cylinder-adapters',
          name: 'cylinderAdapters',
          component: CylinderAdaptersPage,
          meta: { requiresAuth: true, moduleKey: 'cilindros' },
          children: [
            { path: 'list', name: 'cylinderAdaptersList', component: CylinderAdaptersList, meta },
            {
              path: 'detail/:id',
              name: 'cylinderAdapterDetail',
              component: CylinderAdapterDetail,
              meta,
            },
            {
              path: 'edit/:id?',
              name: 'cylinderAdapterEdit',
              component: CylinderAdapterEdit,
              meta,
            },
          ],
        },
        {
          path: 'swage',
          name: 'swages',
          component: SwagePage,
          meta: { requiresAuth: true, moduleKey: 'suajes' },
          children: [
            { path: 'list', name: 'swage', component: SwageList, meta },
            { path: 'edit/:id', name: 'swageEdit', component: SwageEdit, meta },
            { path: 'detail/:id', name: 'swageDetail', component: SwageDetail, meta },
          ],
        },
        {
          path: 'setting',
          name: 'setting',
          component: SettingPage,
          meta: { requiresAuth: true, moduleKey: 'configuracion-general' },
          children: [
            // Configuración General
            {
              path: 'general',
              name: 'settingGeneral',
              component: SettingList,
            },

            // Parámetros
            {
              path: 'custom-product',
              name: 'settingCustomProduct',
              component: () =>
                import('@/views/setting/components/custom_product/custom_products_view.vue'),
            },
            {
              path: 'element-group',
              name: 'settingElementGroup',
              component: () =>
                import('@/views/setting/components/element_group/element_group_view.vue'),
            },
            {
              path: 'monedas',
              name: 'settingMonedas',
              component: () => import('@/views/setting/components/monedas/monedas_view.vue'),
            },
            {
              path: 'apis',
              name: 'settingApis',
              component: () => import('@/views/setting/components/apis/apis_view.vue'),
            },
            {
              path: 'attributes',
              name: 'settingAttributes',
              component: () =>
                import('@/views/setting/components/atributos-dinamicos/atributos_dinamicos_view.vue'),
            },
            {
              path: 'conceptos',
              name: 'settingConceptos',
              component: () =>
                import('@/views/setting/components/additional_settings/conceptos_view.vue'),
            },
            {
              path: 'depositos',
              name: 'settingDepositos',
              component: () =>
                import('@/views/setting/components/additional_settings/depositos_view.vue'),
            },
            {
              path: 'talonarios',
              name: 'settingTalonarios',
              component: () =>
                import('@/views/setting/components/additional_settings/talonarios_view.vue'),
            },
            {
              path: 'ubicaciones',
              name: 'settingUbicaciones',
              component: () =>
                import('@/views/setting/components/additional_settings/ubicaciones_view.vue'),
            },
            {
              path: 'roles',
              name: 'settingRoles',
              component: () => import('@/views/setting/components/roles_settings/roles_view.vue'),
            },
            {
              path: 'generate-files',
              name: 'settingGenerateFiles',
              component: () =>
                import('@/views/setting/components/generate_files/generate_files_view.vue'),
            },
            {
              path: 'inventory',
              name: 'settingInventory',
              component: () => import('@/views/setting/components/inventory/inventory_view.vue'),
            },

            // Procesos
            {
              path: 'import-clients',
              name: 'importClients',
              component: () => import('@/views/setting/imports/import_clients_view.vue'),
            },
            {
              path: 'import-materials',
              name: 'importMaterials',
              component: () => import('@/views/setting/imports/import_materials_view.vue'),
            },
            {
              path: 'import-swages',
              name: 'importSwages',
              component: () => import('@/views/setting/imports/import_swages_view.vue'),
            },
          ],
        },
        {
          path: 'users',
          name: 'users',
          component: UsersPage,
          meta: { requiresAuth: true, moduleKey: 'usuarios' },
          children: [
            { path: 'list', name: 'user', component: UserList },
            { path: 'detail/:id', name: 'userDetail', component: UserDetail },
            { path: 'edit/:id?', name: 'userEdit', component: UserEdit },
          ],
        },
        {
          path: 'material',
          name: 'material',
          component: MaterialPage,
          meta: { requiresAuth: true, moduleKey: 'materiales' },
          children: [
            { path: 'list', name: 'materialList', component: MaterialList },
            { path: 'detail/:id', name: 'materialDetail', component: MaterialDetail },
            { path: 'edit/:id?', name: 'materialEdit', component: MaterialEdit },
          ],
        },
        {
          path: 'reports',
          name: 'reports',
          component: ReportsPage,
          meta: { requiresAuth: true, moduleKey: 'reportes' },
          children: [{ path: 'list', name: 'reportsList', component: ReportsList }],
        },
        {
          path: 'work-orders',
          name: 'workOrders',
          component: WorkOrderPage,
          meta: { requiresAuth: true, moduleKey: 'orden-produccion' },
          children: [
            { path: 'list', name: 'workOrderList', component: WorkOrderList, props: true },
            { path: 'edit/:id?', name: 'workOrderEdit', component: WorkOrderEdit },
            {
              path: 'resultado-produccion/:codigo',
              name: 'vistaGeneralProduccionOT',
              component: VistaGeneralProduccionOT,
            },
          ],
        },
        {
          path: 'technical-specifications',
          name: 'technicalSpecifications',
          component: TechnicalSpecificationPage,
          meta: { requiresAuth: true, moduleKey: 'especificacion-tecnica' },
          children: [
            {
              path: 'list',
              name: 'technicalSpecificationList',
              component: TechnicalSpecificationList,
              props: true,
            },
            {
              path: 'edit/:id?',
              name: 'technicalSpecificationEdit',
              component: TechnicalSpecificationEdit,
            },
          ],
        },
        {
          path: 'tipo-tratado',
          name: 'tipoTratado',
          component: TipoTratadoPage,
          meta: { requiresAuth: true, moduleKey: 'tipo-tratado' },
          children: [
            { path: 'list', name: 'tipoTratadoList', component: TipoTratadoList },
            { path: 'edit/:id?', name: 'tipoTratadoEdit', component: TipoTratadoEdit },
          ],
        },
        {
          path: 'colores',
          name: 'colores',
          component: ColoresPage,
          meta: { requiresAuth: true, moduleKey: 'colores' },
          children: [{ path: 'list', name: 'coloresList', component: ColoresList }],
        },
        {
          path: 'pallet',
          name: 'pallet',
          component: () => import('@/views/pallet/pallet_page.vue'),
          meta: { requiresAuth: true, moduleKey: 'movimientos-wip' },
          children: [
            {
              path: 'list',
              name: 'palletList',
              component: () => import('@/views/pallet/pallet_list/pallet_list.vue'),
            },
            // { path: 'scan', name: 'palletScan', component: () => import('@/views/pallet/pallet_scan/pallet_scan.vue') }
          ],
        },
        // Traslado de Stock por Escaneo (COTI-594): nested como cualquier otra página (mismo
        // patrón que monitor-tv) para heredar el header móvil compartido (hamburguesa de
        // navegación) de PageContainer, en vez de construir el suyo propio. Antes era una ruta
        // standalone (ver git history) porque el paso con cámara necesitaba la pantalla completa;
        // ya no hay cámara en este flujo, así que esa razón dejó de aplicar. Usa la misma
        // estructura que el resto de los módulos: página contenedora con RouterView y la vista
        // como hija (path vacío, así la URL sigue siendo /page/traslado-stock).
        {
          path: 'traslado-stock',
          component: () => import('@/views/traslado/traslado_stock_page.vue'),
          meta: { requiresAuth: true, moduleKey: 'traslado-stock' },
          children: [
            {
              path: '',
              name: 'trasladoStock',
              component: () => import('@/views/traslado/traslado_page.vue'),
            },
          ],
        },
        {
          path: 'monitor',
          name: 'monitor',
          component: () => import('@/views/monitor/pages/monitor_page.vue'),
          meta: { requiresAuth: true, moduleKey: 'monitor-tv' },
          children: [
            {
              path: 'pendientes',
              name: 'pendientes',
              component: () => import('@/views/monitor/pages/PendingOrders.vue'),
              meta: { title: 'Pendientes' },
            },
            {
              path: 'programadas',
              name: 'programadas',
              component: () => import('@/views/monitor/pages/ScheduledOrders.vue'),
              meta: { title: 'Programadas' },
            },
            {
              path: 'en-curso',
              name: 'en-curso',
              component: () => import('@/views/monitor/pages/InProgressOrders.vue'),
              meta: { title: 'En curso' },
            },
            {
              path: 'finalizadas',
              name: 'finalizadas',
              component: () => import('@/views/monitor/pages/CompletedOrders.vue'),
              meta: { title: 'Finalizadas' },
            },
            {
              path: 'orden/:id/:subid',
              name: 'detalle-orden',
              component: () => import('@/views/monitor/pages/OrderDetail.vue'),
              meta: { title: 'Detalle' },
            },
            {
              path: 'informe-recurso',
              name: 'informe-recurso',
              component: () => import('@/views/monitor/pages/MachineReport.vue'),
              meta: { title: 'Informe por Recurso' },
            },
            {
              path: 'monitor-tv',
              name: 'monitor-tv',
              component: () => import('@/views/monitor/pages/MachineMonitorTV.vue'),
              meta: { title: 'Monitor TV', noScroll: true },
            },
          ],
        },
        {
          path: 'supplier',
          name: 'supplier',
          component: () => import('@/views/supplier/supplier_page.vue'),
          meta: { requiresAuth: true, moduleKey: 'proveedor' },
          children: [
            {
              path: 'list',
              name: 'supplierList',
              component: () => import('@/views/supplier/supplier_list/supplier_list.vue'),
            },
            {
              path: 'edit/:id?',
              name: 'supplierEdit',
              component: () => import('@/views/supplier/supplier_edit/supplier_edit.vue'),
            },
          ],
        },
        {
          path: 'agent',
          name: 'agent',
          component: () => import('@/views/agent/agent_page.vue'),
          meta: { requiresAuth: true, moduleKey: 'agente' },
          children: [
            {
              path: 'list',
              name: 'agentList',
              component: () => import('@/views/agent/agent_list/agent_list.vue'),
            },
            {
              path: 'edit/:id?',
              name: 'agentEdit',
              component: () => import('@/views/agent/agent_edit/agent_edit.vue'),
            },
          ],
        },
        {
          path: 'configuraciones-por-defecto',
          name: 'configuracionesPorDefecto',
          component: () =>
            import('@/views/configuraciones_por_defecto/configuraciones_por_defecto_page.vue'),
          meta: { requiresAuth: true, moduleKey: 'configuraciones-por-defecto' },
          children: [
            {
              path: 'list',
              name: 'configuracionesPorDefectoList',
              component: () =>
                import('@/views/configuraciones_por_defecto/configuraciones_por_defecto_list/configuraciones_por_defecto_list.vue'),
            },
          ],
        },
        {
          path: 'inventory',
          name: 'inventory',
          component: () => import('@/views/inventory/inventory_page.vue'),
          meta: { requiresAuth: true, moduleKey: 'stock' },
          children: [
            {
              path: 'list',
              name: 'inventoryList',
              component: () => import('@/views/inventory/inventory_list/inventory_list.vue'),
            },
            {
              path: 'edit/:id?',
              name: 'inventoryEdit',
              component: () => import('@/views/inventory/inventory_edit/inventory_edit.vue'),
            },
            {
              path: 'detail/:id?',
              name: 'inventoryDetail',
              component: () => import('@/views/inventory/inventory_detail/inventory_detail.vue'),
            },
          ],
        },
        {
          path: 'inventory-movement',
          name: 'inventory-movement',
          component: () => import('@/views/inventory_movement/inventory_movement_page.vue'),
          meta: { requiresAuth: true, moduleKey: 'stock' },
          children: [
            {
              path: 'list',
              name: 'inventoryMovementList',
              component: () =>
                import('@/views/inventory_movement/inventory_movement_list/inventory_movement_list.vue'),
            },
            {
              path: 'edit/:id?',
              name: 'inventoryMovementEdit',
              component: () =>
                import('@/views/inventory_movement/inventory_movement_edit/inventory_movement_edit.vue'),
            },
            {
              path: 'detail/:id?',
              name: 'inventoryMovementDetail',
              component: () =>
                import('@/views/inventory_movement/inventory_movement_detail/inventory_movement_detail.vue'),
            },
          ],
        },
      ],
    },
    // Rutas de pallet independientes para evitar doble PageContainer
    {
      path: '/page/pallet/scan',
      name: 'palletScan',
      component: () => import('@/views/pallet/pallet_scan/pallet_scan.vue'),
      meta: { requiresAuth: true, moduleKey: 'movimientos-wip' },
    },
    {
      path: '/page/pallet/hid-scan',
      name: 'palletHidScan',
      component: () => import('@/views/pallet/pallet_hid_scan/pallet_hid_scan.vue'),
      meta: { requiresAuth: true, moduleKey: 'movimientos-wip' },
    },
  ],
});

// Función para encontrar la primera ruta a la que el usuario tiene acceso
const getFirstAllowedRoute = (usuario) => {
  const routesMap = [
    { path: '/dashboard', key: 'prospectos' },
    { path: '/page/requests/list', key: 'solicitud' },
    { path: '/page/prices/list', key: 'cotizacion' },
    { path: '/page/proposals/list', key: 'propuestas' },
    { path: '/page/technical-specifications/list', key: 'especificacion-tecnica' },
    { path: '/page/work-orders/list', key: 'orden-produccion' },
    { path: '/page/resource/list', key: 'recursos' },
    { path: '/page/cylinders/list', key: 'cilindros' },
    { path: '/page/swage/list', key: 'suajes' },
    { path: '/page/material/list', key: 'materiales' },
    { path: '/page/users/list', key: 'usuarios' },
    { path: '/page/setting/general', key: 'configuracion-general' },
    { path: '/page/configuraciones-por-defecto/list', key: 'configuraciones-por-defecto' },
    { path: '/page/pallet/list', key: 'movimientos-wip' },
    { path: '/page/traslado-stock', key: 'traslado-stock' },
    { path: '/page/agent/list', key: 'agente' },
    { path: '/page/supplier/list', key: 'proveedor' },
    { path: '/page/inventory/list', key: 'stock' },
    { path: '/page/colores/list', key: 'colores' },
    { path: '/page/tipo-tratado/list', key: 'tipo-tratado' },
  ];

  for (const r of routesMap) {
    if (canViewModule(usuario, r.key)) {
      return r.path;
    }
  }

  return '/home';
};

router.beforeEach(async (to, from, next) => {
  // Cerrar menú móvil al navegar
  try {
    const { closeMobileMenu } = useMobileMenu();
    closeMobileMenu();
  } catch (error) {
    // Si hay error con el composable, continuamos sin cerrar el menú
  }

  const authStore = useAuthStore();
  const { requiresAuth, roles: rolesRequeridos } = to.meta;
  const hasToken = authStore.hasToken();
  const usuario = authStore.usuario();
  let withAuthorization = false;

  if (!requiresAuth && requiresAuth === false) {
    next();
    return;
  }

  if (hasToken) {
    if (!usuario) {
      withAuthorization = false;
    } else {
      const moduleKey = to.meta?.moduleKey;
      if (moduleKey) {
        withAuthorization = canViewModule(usuario, moduleKey);
        if (
          withAuthorization &&
          to.name === 'technicalSpecificationEdit' &&
          !to.params?.id &&
          !canCreateModule(usuario, moduleKey)
        ) {
          next({ path: '/page/technical-specifications/list' });
          return;
        }
      } else {
        if (rolesRequeridos && rolesRequeridos.length > 0) {
          const rolesUsuario = (usuario.roles ?? []).map((r) => r.Codigo);
          withAuthorization =
            rolesRequeridos.indexOf(0) >= 0 ||
            rolesRequeridos.some((rr) => rolesUsuario.includes(rr));
        } else {
          withAuthorization = true;
        }
      }
    }
  }

  if (!withAuthorization) {
    if (hasToken) {
      const fallbackRoute = getFirstAllowedRoute(usuario);
      if (to.path !== fallbackRoute) {
        next({ path: fallbackRoute });
      } else {
        next('/home');
      }
    } else {
      next({
        path: '/',
        query: { redirect: to.fullPath },
      });
    }
  } else {
    next();
  }
});
export default router;
